/**
 * Admin CMS API module — CON-CMS-001 / CON-CMS-002.
 *
 * Provides typed functions for admin-facing CMS page endpoints. Containers
 * and Redux thunks MUST import from this module — never call the shared
 * axios client directly.
 *
 * Endpoints covered:
 *   GET    /api/v1/admin/cms/pages            — paginated list with filters
 *   GET    /api/v1/admin/cms/pages/:id        — single page for editing (CON-CMS-002)
 *   POST   /api/v1/admin/cms/pages            — create page (CON-CMS-002)
 *   PUT    /api/v1/admin/cms/pages/:id        — full update (CON-CMS-002)
 *   DELETE /api/v1/admin/cms/pages/:id        — permanent delete
 *   POST   /api/v1/admin/cms/pages/:id/duplicate — clone a page
 *   GET    /api/v1/admin/cms/pages/:id/revisions — revision history (CON-CMS-002)
 *   POST   /api/v1/admin/cms/pages/:id/revisions/:revId/restore — restore (CON-CMS-002)
 *   GET    /api/v1/admin/cms/testimonials     — list all testimonials (CON-CMS-005)
 *   POST   /api/v1/admin/cms/testimonials     — create testimonial (CON-CMS-005)
 *   PUT    /api/v1/admin/cms/testimonials/:id — update testimonial (CON-CMS-005)
 *   DELETE /api/v1/admin/cms/testimonials/:id — delete testimonial (CON-CMS-005)
 *   PATCH  /api/v1/admin/cms/testimonials/reorder — reorder (CON-CMS-005)
 */
import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError, unwrap } from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  CmsPageEditDTO,
  CmsPageListItemDTO,
  CmsPageListResponse,
  CmsPageRevisionDTO,
  CmsPageStatus,
  CreateCmsPagePayload,
  FetchCmsPagesParams,
  UpdateCmsPageFullPayload,
  TestimonialDTO,
  CreateTestimonialPayload,
  UpdateTestimonialPayload,
  ReorderTestimonialsPayload,
} from '@dfq/shared/types/cms';

// ---------------------------------------------------------------------------
// Wire shape → view shape
//
// `CmsPageDTO::toArray()` emits camelCase with the SEO fields nested under
// `seo`; the views read flat snake_case. The writes go the other way — the
// platform's FormRequests take snake_case. Both projections live here so a
// container never sees the difference.
// ---------------------------------------------------------------------------

interface CmsPageWire {
  id: number;
  title: string;
  slug: string;
  bodyHtml?: string | null;
  status: CmsPageStatus;
  publishedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  ctaLabel?: string | null;
  ctaUrl?: string | null;
  seo?: {
    metaTitle?: string | null;
    metaDescription?: string | null;
    ogImagePath?: string | null;
    canonicalUrl?: string | null;
    robotsDirective?: string | null;
  } | null;
}

function toPageEditDTO(wire: CmsPageWire): CmsPageEditDTO {
  return {
    id: wire.id,
    title: wire.title,
    slug: wire.slug,
    status: wire.status,
    body_html: wire.bodyHtml ?? '',
    meta_title: wire.seo?.metaTitle ?? null,
    meta_description: wire.seo?.metaDescription ?? null,
    og_image_path: wire.seo?.ogImagePath ?? null,
    published_at: wire.publishedAt ?? null,
    created_at: wire.createdAt ?? '',
    updated_at: wire.updatedAt ?? '',
    cta_label: wire.ctaLabel ?? null,
    cta_url: wire.ctaUrl ?? null,
  };
}

interface CmsPageRevisionWire {
  id: number;
  bodyHtml?: string | null;
  createdAt?: string | null;
  editedByUserId?: number | null;
  editorFirstName?: string | null;
  editorLastName?: string | null;
}

function toRevisionDTO(wire: CmsPageRevisionWire): CmsPageRevisionDTO {
  return {
    id: wire.id,
    body_html: wire.bodyHtml ?? '',
    created_at: wire.createdAt ?? '',
    editor: {
      id: wire.editedByUserId ?? 0,
      // A revision written by a since-deleted account has no name to show.
      first_name: wire.editorFirstName ?? 'Unknown',
      last_name: wire.editorLastName ?? '',
    },
  };
}

function toPageListItem(wire: CmsPageWire): CmsPageListItemDTO {
  return {
    id: wire.id,
    title: wire.title,
    slug: wire.slug,
    status: wire.status,
    meta_title: wire.seo?.metaTitle ?? null,
    published_at: wire.publishedAt ?? null,
    updated_at: wire.updatedAt ?? '',
  };
}

// ---------------------------------------------------------------------------
// GET /api/v1/admin/cms/pages
// ---------------------------------------------------------------------------

/**
 * Fetch a paginated, filtered list of CMS pages for the admin list view.
 *
 * Supports `search`, `status`, `page`, `per_page`, `sort`, and `direction`
 * query params as defined by the platform `CmsPagesController@index` action.
 */
export async function fetchAdminCmsPages(
  params: FetchCmsPagesParams = {},
): Promise<CmsPageListResponse> {
  try {
    const res = await apiClient.get<ApiEnvelope<{ pages: CmsPageWire[] }>>(
      '/admin/cms/pages',
      { params },
    );
    const data = unwrap(res.data, res.status);
    // Pagination rides on the envelope's `meta`, not inside `data`.
    const pagination = res.data.meta?.pagination as
      | { page?: number; per_page?: number; total?: number; last_page?: number }
      | undefined;
    return {
      items: (data.pages ?? []).map(toPageListItem),
      meta: {
        current_page: pagination?.page ?? 1,
        per_page: pagination?.per_page ?? data.pages?.length ?? 0,
        total: pagination?.total ?? data.pages?.length ?? 0,
        last_page: pagination?.last_page ?? 1,
      },
    };
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// PUT /api/v1/admin/cms/pages/:id (status toggle — CON-CMS-002 owns full edit)
// ---------------------------------------------------------------------------

export interface UpdateCmsPagePayload {
  status?: CmsPageStatus;
  title?: string;
  slug?: string;
}

/**
 * Partial-update a CMS page. Used by the inline status toggle on the list view.
 *
 * The full-edit endpoint (body_html, SEO fields, etc.) is owned by CON-CMS-002
 * but the status toggle only needs `{ status }`. Both use this function.
 *
 * Backend endpoint: PUT /api/v1/admin/cms/pages/:id (CON-CMS-002 ships full impl).
 */
export async function updateAdminCmsPage(
  id: number,
  payload: UpdateCmsPagePayload,
): Promise<CmsPageListItemDTO> {
  try {
    const res = await apiClient.put<ApiEnvelope<{ page: CmsPageWire }>>(
      `/admin/cms/pages/${id}`,
      payload,
    );
    return toPageListItem(unwrap(res.data, res.status).page);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// DELETE /api/v1/admin/cms/pages/:id
// ---------------------------------------------------------------------------

/**
 * Permanently delete a CMS page.
 *
 * Backend: DeleteCmsPageAction validates ownership, executes deletion, triggers
 * audit logging, and fires a PageDeleted event.
 */
export async function deleteAdminCmsPage(id: number): Promise<void> {
  try {
    await apiClient.delete(`/admin/cms/pages/${id}`);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// POST /api/v1/admin/cms/pages/:id/duplicate
// ---------------------------------------------------------------------------

/**
 * Clone a CMS page. The platform appends " (Copy)" to the title and generates
 * a unique slug. Returns the newly-created page list-item.
 *
 * Backend: forward dependency (CON-CMS-002 companion action). The route and
 * action exist as stubs until that ticket lands.
 */
export async function duplicateAdminCmsPage(
  id: number,
): Promise<CmsPageListItemDTO> {
  try {
    const res = await apiClient.post<ApiEnvelope<{ page: CmsPageWire }>>(
      `/admin/cms/pages/${id}/duplicate`,
    );
    return toPageListItem(unwrap(res.data, res.status).page);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// GET /api/v1/admin/cms/pages/:id  (CON-CMS-002)
// ---------------------------------------------------------------------------

/**
 * Fetch a single CMS page for the editor view.
 *
 * Returns the full edit DTO including body_html, SEO fields, og_image_path,
 * and timestamps. Used by `PageEditorView` when mounting the edit route.
 */
export async function fetchAdminCmsPage(id: number): Promise<CmsPageEditDTO> {
  try {
    const res = await apiClient.get<ApiEnvelope<{ page: CmsPageWire }>>(
      `/admin/cms/pages/${id}`,
    );
    return toPageEditDTO(unwrap(res.data, res.status).page);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// POST /api/v1/admin/cms/pages  (CON-CMS-002)
// ---------------------------------------------------------------------------

/**
 * Create a new CMS page.
 *
 * Returns the full edit DTO of the newly created page. The container uses
 * the returned id to navigate to `/admin/cms/pages/:id/edit` with replace.
 *
 * Backend dependency: POST /api/v1/admin/cms/pages (PL-CMS-002).
 */
export async function createAdminCmsPage(
  payload: CreateCmsPagePayload,
): Promise<CmsPageEditDTO> {
  try {
    const res = await apiClient.post<ApiEnvelope<{ page: CmsPageWire }>>(
      '/admin/cms/pages',
      payload,
    );
    return toPageEditDTO(unwrap(res.data, res.status).page);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// PUT /api/v1/admin/cms/pages/:id (full update — CON-CMS-002)
// ---------------------------------------------------------------------------

/**
 * Full update of a CMS page (body_html, title, slug, SEO, publish state).
 *
 * Returns the updated page edit DTO. Auto-save and explicit Save both use
 * this endpoint.
 *
 * Backend dependency: PUT /api/v1/admin/cms/pages/:id (PL-CMS-002).
 */
export async function updateAdminCmsPageFull(
  id: number,
  payload: UpdateCmsPageFullPayload,
): Promise<CmsPageEditDTO> {
  try {
    const res = await apiClient.put<ApiEnvelope<{ page: CmsPageWire }>>(
      `/admin/cms/pages/${id}`,
      payload,
    );
    return toPageEditDTO(unwrap(res.data, res.status).page);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// GET /api/v1/admin/cms/pages/:id/revisions  (CON-CMS-002)
// ---------------------------------------------------------------------------

/**
 * Fetch the revision history for a CMS page in reverse-chronological order.
 *
 * Backend dependency: GET /api/v1/admin/cms/pages/:id/revisions (PL-CMS-003).
 */
export async function fetchAdminCmsPageRevisions(
  id: number,
): Promise<CmsPageRevisionDTO[]> {
  try {
    const res = await apiClient.get<ApiEnvelope<{ revisions: CmsPageRevisionWire[] }>>(
      `/admin/cms/pages/${id}/revisions`,
    );
    const data = unwrap(res.data, res.status);
    return (data.revisions ?? []).map(toRevisionDTO);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// POST /api/v1/admin/cms/pages/:id/revisions/:revId/restore  (CON-CMS-002)
// ---------------------------------------------------------------------------

/**
 * Restore a page to a previous revision's body_html.
 *
 * Returns the updated page edit DTO with the restored body_html. The
 * container uses this to populate the editor and marks isDirty=true so the
 * user must explicitly save.
 *
 * Backend dependency: POST /api/v1/admin/cms/pages/:id/revisions/:revId/restore (PL-CMS-003).
 */
export async function restoreAdminCmsPageRevision(
  pageId: number,
  revisionId: number,
): Promise<CmsPageEditDTO> {
  try {
    const res = await apiClient.post<ApiEnvelope<{ page: CmsPageWire }>>(
      `/admin/cms/pages/${pageId}/revisions/${revisionId}/restore`,
    );
    return toPageEditDTO(unwrap(res.data, res.status).page);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// Testimonials — CON-CMS-005
// Endpoints: GET/POST /api/v1/admin/cms/testimonials
//            GET/PUT/DELETE /api/v1/admin/cms/testimonials/:id
//            PATCH /api/v1/admin/cms/testimonials/reorder
// ---------------------------------------------------------------------------

/** Fetch all testimonials (no pagination — list is typically small). */
export async function fetchAdminTestimonials(): Promise<TestimonialDTO[]> {
  try {
    const res = await apiClient.get<ApiEnvelope<{ testimonials: TestimonialDTO[] }>>(
      '/admin/cms/testimonials',
    );
    return unwrap(res.data, res.status).testimonials;
  } catch (err) {
    throwAsApiError(err);
  }
}

/** Create a new testimonial. */
export async function createAdminTestimonial(
  payload: CreateTestimonialPayload,
): Promise<TestimonialDTO> {
  try {
    const res = await apiClient.post<ApiEnvelope<{ testimonial: TestimonialDTO }>>(
      '/admin/cms/testimonials',
      payload,
    );
    return unwrap(res.data, res.status).testimonial;
  } catch (err) {
    throwAsApiError(err);
  }
}

/** Update an existing testimonial. */
export async function updateAdminTestimonial(
  id: number,
  payload: UpdateTestimonialPayload,
): Promise<TestimonialDTO> {
  try {
    const res = await apiClient.put<ApiEnvelope<{ testimonial: TestimonialDTO }>>(
      `/admin/cms/testimonials/${id}`,
      payload,
    );
    return unwrap(res.data, res.status).testimonial;
  } catch (err) {
    throwAsApiError(err);
  }
}

/** Permanently delete a testimonial. */
export async function deleteAdminTestimonial(id: number): Promise<void> {
  try {
    await apiClient.delete(`/admin/cms/testimonials/${id}`);
  } catch (err) {
    throwAsApiError(err);
  }
}

/** Reorder testimonials by submitting the full ordered_ids array. */
export async function reorderAdminTestimonials(
  payload: ReorderTestimonialsPayload,
): Promise<void> {
  try {
    await apiClient.patch('/admin/cms/testimonials/reorder', payload);
  } catch (err) {
    throwAsApiError(err);
  }
}

export const cmsAdminApiClient = {
  fetchPages: fetchAdminCmsPages,
  fetchPage: fetchAdminCmsPage,
  createPage: createAdminCmsPage,
  updatePage: updateAdminCmsPage,
  updatePageFull: updateAdminCmsPageFull,
  deletePage: deleteAdminCmsPage,
  duplicatePage: duplicateAdminCmsPage,
  fetchRevisions: fetchAdminCmsPageRevisions,
  restoreRevision: restoreAdminCmsPageRevision,
  fetchTestimonials: fetchAdminTestimonials,
  createTestimonial: createAdminTestimonial,
  updateTestimonial: updateAdminTestimonial,
  deleteTestimonial: deleteAdminTestimonial,
  reorderTestimonials: reorderAdminTestimonials,
};
